/**
 * The ids of the rows still open in the DataGrid's row-edit mode that hold a value differing from
 * the row they edit. An inline child grid commits a row only when its edit stops (Enter, Tab past
 * the last cell, a click elsewhere); these rows are unsaved before that, so the grid tints them
 * and the host view enables Save from the first changed value.
 *
 * `editRows` is the grid's `state.editRows`: row id → field → cell edit state.
 */
export function changedEditRowIds(
  editRows: Record<string, Record<string, { value?: unknown }>> | undefined,
  rows: readonly Record<string, unknown>[],
): ReadonlySet<string> {
  const ids = new Set<string>();
  if (!editRows) return ids;
  for (const [rowId, cells] of Object.entries(editRows)) {
    const row = rows.find((r) => String(r.id) === rowId);
    if (!row || !cells) continue;
    if (Object.entries(cells).some(([field, cell]) => !sameValue(cell?.value, row[field]))) ids.add(rowId);
  }
  return ids;
}

/**
 * The ids of the rows a child collection holds that are not saved yet: rows the saved collection
 * does not have, and rows whose values differ from their saved version. Lets a grid whose rows
 * all live in the parent's form mark them like a paged grid marks its change set. A removed row
 * is simply gone from the grid, so it has nothing to mark.
 */
export function unsavedRowIds(
  current: readonly object[] | null | undefined,
  saved: readonly object[] | null | undefined,
): ReadonlySet<string> {
  const ids = new Set<string>();
  const savedById = new Map(asRows(saved).map((row) => [String(row.id), row]));
  for (const row of asRows(current)) {
    const original = savedById.get(String(row.id));
    if (!original || !sameRow(row, original)) ids.add(String(row.id));
  }
  return ids;
}

/** A collection's rows read field by field: an entity's generated interface has no index signature. */
function asRows(rows: readonly object[] | null | undefined): readonly Record<string, unknown>[] {
  return (rows ?? []) as readonly Record<string, unknown>[];
}

/**
 * The ids of the rows a child collection holds that the saved collection does not have: rows added since the
 * parent was loaded, whether typed in the grid or copied in. Such a row is created by the next save, so a field
 * its create may set and its update may not (`includeInUpdateDto: false`, a code say) is still the user's to set.
 */
export function newRowIds(
  current: readonly Record<string, unknown>[] | null | undefined,
  saved: readonly Record<string, unknown>[] | null | undefined,
): ReadonlySet<string> {
  const savedIds = new Set((saved ?? []).map((row) => String(row.id)));
  return new Set((current ?? []).map((row) => String(row.id)).filter((id) => !savedIds.has(id)));
}

function sameRow(row: Record<string, unknown>, original: Record<string, unknown>): boolean {
  const fields = new Set([...Object.keys(row), ...Object.keys(original)]);
  for (const field of fields) {
    if (!sameValue(row[field], original[field])) return false;
  }
  return true;
}

/** Empty is empty however it is spelled; a typed number equals the number it spells. */
export function sameValue(edited: unknown, saved: unknown): boolean {
  const a = normalize(edited);
  const b = normalize(saved);
  if (a === b) return true;
  return typeof a === 'object' || typeof b === 'object' ? JSON.stringify(a) === JSON.stringify(b) : String(a) === String(b);
}

function normalize(value: unknown): unknown {
  if (value === undefined || value === null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return value;
}
